import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart } from 'chart.js/auto';

Chart.register(annotationPlugin);

// Chart.js renders to a <canvas>, which can't resolve CSS custom properties
// the way DOM elements can -- so this can't just import theme.js and let the
// var() cascade handle light mode. The dark-mode line/axis colors (~2.8:1 on
// a white card) fail WCAG AA there, so the two values that matter for
// readability are picked once, at chart-build time, via matchMedia.
const prefersLight = typeof window !== 'undefined'
  && window.matchMedia
  && window.matchMedia('(prefers-color-scheme: light)').matches;

const chartGold = prefersLight ? '#7d5f2e' : '#b8955a';
const chartTextMuted = prefersLight ? '#6b6558' : '#8a9bb0';
const chartGridColor = prefersLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)';

const buildChartConfig = (data) => {
  const labels = data.map(d => d.date);
  const values = data.map(d => d.value);

  const getIndex = (yearMonth) => labels.findIndex(l => l.startsWith(yearMonth));

  // Colors match the accent used by each era's card in the "Reading the
  // Chart" grid below, so the shaded chart regions and their write-ups
  // read as one linked system. COVID is colored to match "Post-GFC: A
  // Decade Near Zero" since that card's 2009-2021 span is what covers it.
  const dotcomColor = '214,138,76';
  const gfcColor = '214,63,63';
  const covidColor = '91,141,190';

  const annotations = {
    dotcomBox: {
      type: 'box',
      xMin: getIndex('2001-03'),
      xMax: getIndex('2001-11'),
      backgroundColor: `rgba(${dotcomColor},0.08)`,
      borderColor: `rgba(${dotcomColor},0.3)`,
      borderWidth: 1,
    },
    dotcomLabel: {
      type: 'label',
      xValue: getIndex('2001-07'),
      yValue: Math.max(...values) * 0.95,
      content: ['Dot-com', 'Bust'],
      font: { size: 13 },
      color: `rgba(${dotcomColor},0.95)`,
      textAlign: 'center'
    },
    gfcBox: {
      type: 'box',
      xMin: getIndex('2007-12'),
      xMax: getIndex('2009-06'),
      backgroundColor: `rgba(${gfcColor},0.08)`,
      borderColor: `rgba(${gfcColor},0.3)`,
      borderWidth: 1,
    },
    gfcLabel: {
      type: 'label',
      xValue: getIndex('2008-09'),
      yValue: Math.max(...values) * 0.95,
      content: ['GFC', 'Crisis'],
      font: { size: 13 },
      color: `rgba(${gfcColor},0.95)`,
      textAlign: 'center'
    },
    covidBox: {
      type: 'box',
      xMin: getIndex('2020-02'),
      xMax: getIndex('2020-04'),
      backgroundColor: `rgba(${covidColor},0.08)`,
      borderColor: `rgba(${covidColor},0.3)`,
      borderWidth: 1,
    },
    covidLabel: {
      type: 'label',
      xValue: getIndex('2020-03'),
      yValue: Math.max(...values) * 0.95,
      content: ['COVID'],
      font: { size: 13 },
      color: `rgba(${covidColor},0.95)`,
      textAlign: 'center'
    }
  };

  return {
    type: 'line',
    data: {
      labels,
      datasets: [{
        label: 'Fed Funds Rate',
        data: values,
        borderColor: chartGold,
        backgroundColor: 'rgba(184,149,90,0.08)',
        fill: true,
        pointRadius: 0,
        borderWidth: 1.5,
        tension: 0.2
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: ctx => ctx.parsed.y.toFixed(2) + '%'
          }
        },
        annotation: { annotations }
      },
      scales: {
        x: {
          grid: {
            color: chartGridColor
          },
          ticks: {
            color: chartTextMuted,
            font: { size: 13 },
            autoSkip: true,
            autoSkipPadding: 24,
            maxRotation: 0,
            callback: (val, idx) => labels[idx]?.substring(0, 4)
          }
        },
        y: {
          grid: {
            color: chartGridColor
          },
          ticks: {
            color: chartTextMuted,
            font: { size: 13 },
            callback: v => v.toFixed(1) + '%'
          }
        }
      }
    }
  };
};

export default buildChartConfig;